import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Header } from '@/app/layout/Header';
import { Main } from '@/app/layout/Main';
import { Button, confirm, toast } from '@devhop/ui';
import { HelpCircle, Plus } from 'lucide-react';
import { usePermissions } from '@/modules/auth/presentation/model/usePermissions';
import { Permissions } from '@/modules/roles/domain/contracts/permissions';
import type { CompanyPolicyListItem } from '@/modules/hr/company-policy/domain/types';
import {
    useCompanyPoliciesQuery,
    useDeleteCompanyPolicyMutation,
    useUpdateCompanyPolicyStatusMutation,
} from '../api/queries';
import { PolicyListTour, usePolicyListTour } from '../tour/PolicyListTour';
import { PolicyFilter, type PolicySearchParams } from '../ui/PolicyFilter';
import { PolicyTable } from '../ui/PolicyTable';

export function PolicyListPage() {
    const nav = useNavigate({ from: '/app/hr/company-policy' });
    const search = useSearch({
        from: '/app/hr/company-policy',
    }) as PolicySearchParams & {
        offset?: number;
        limit?: number;
        sort?: Array<{ field: string; dir: 'asc' | 'desc' }>;
    };

    const sortBy = search.sort?.[0]?.field ?? 'updatedAt';
    const sortOrder = (search.sort?.[0]?.dir ?? 'desc') as 'asc' | 'desc';
    const normalizedStatus =
        search.status === 'draft' || search.status === 'active'
            ? search.status
            : undefined;

    const query = useMemo(
        () => ({
            offset: search.offset ?? 0,
            limit: search.limit ?? 20,
            sort: search.sort,
            policyTypeId: search.policyTypeId,
            status: normalizedStatus,
            search: search.search,
        }),
        [
            search.offset,
            search.limit,
            search.sort,
            search.policyTypeId,
            normalizedStatus,
            search.search,
        ],
    );

    const listQuery = useCompanyPoliciesQuery(query);
    const data = useMemo(
        () =>
            (listQuery.data?.data ?? []).filter(
                (item) => item.status === 'draft' || item.status === 'active',
            ),
        [listQuery.data?.data],
    );
    const meta = listQuery.data?.meta;
    const totalCount = meta?.total ?? 0;
    const limit = meta?.limit ?? 20;
    const offset = search.offset ?? 0;

    const canCreate = usePermissions().has(
        Permissions.hr_company_policy.create,
    );
    const canUpdate = usePermissions().has(
        Permissions.hr_company_policy.update,
    );
    const canDelete = usePermissions().has(
        Permissions.hr_company_policy.delete,
    );

    const deleteMutation = useDeleteCompanyPolicyMutation();
    const updateStatusMutation = useUpdateCompanyPolicyStatusMutation();
    const tour = usePolicyListTour();

    const handlePaginationChange = (newOffset: number, newLimit: number) => {
        nav({
            search: {
                offset: newOffset,
                limit: newLimit,
                sort: search.sort,
                policyTypeId: search.policyTypeId,
                status: normalizedStatus,
                search: search.search,
            },
        });
    };

    const handleView = (row: CompanyPolicyListItem) => {
        nav({ to: '/app/hr/company-policy/$id', params: { id: row.id } });
    };

    const handleEdit = (row: CompanyPolicyListItem) => {
        nav({ to: '/app/hr/company-policy/$id/edit', params: { id: row.id } });
    };

    const handleDelete = useCallback(
        async (row: CompanyPolicyListItem) => {
            if (row.status !== 'draft') return;
            const ok = await confirm({
                title: 'ລຶບນະໂຍບາຍ?',
                description: `"${row.title}" ຈະຖືກລຶບອອກຖາວອນ. ການກະທຳນີ້ບໍ່ສາມາດກູ້ຄືນໄດ້.`,
                actionText: 'ລຶບ',
                ActionProps: { variant: 'destructive' },
            });
            if (!ok) return;
            deleteMutation.mutate(row.id, {
                onSuccess: () => toast.success('ລຶບນະໂຍບາຍແລ້ວ'),
                onError: () => toast.error('ບໍ່ສາມາດລຶບໄດ້'),
            });
        },
        [deleteMutation],
    );

    const handleArchive = useCallback(
        (row: CompanyPolicyListItem) => {
            if (row.status !== 'draft' && row.status !== 'active') return;
            updateStatusMutation.mutate(
                { id: row.id, dto: { status: 'archived' } },
                {
                    onSuccess: () => toast.success('ປ່ຽນເປັນເກັບໄວ້ແລ້ວ'),
                    onError: () => toast.error('ບໍ່ສາມາດປ່ຽນສະຖານະໄດ້'),
                },
            );
        },
        [updateStatusMutation],
    );

    const handleDuplicate = useCallback(
        (row: CompanyPolicyListItem) => {
            nav({
                to: '/app/hr/company-policy/new',
                search: { duplicateFrom: row.id },
            });
        },
        [nav],
    );

    const handleNewPolicy = () => {
        nav({
            to: '/app/hr/company-policy/new',
            search: { duplicateFrom: undefined },
        });
    };

    const handleSortingChange = useCallback(
        (id: string, desc: boolean) => {
            nav({
                search: {
                    offset: 0,
                    limit: search.limit ?? 20,
                    sort: [
                        { field: id, dir: (desc ? 'desc' : 'asc') as 'asc' | 'desc' },
                    ],
                    policyTypeId: search.policyTypeId,
                    status: normalizedStatus,
                    search: search.search,
                },
            });
        },
        [nav, search.limit, search.policyTypeId, normalizedStatus, search.search],
    );

    return (
        <>
            <Header />
            <Main>
                <PolicyListTour
                    run={tour.run}
                    onCallback={tour.handleJoyrideCallback}
                />
                <div
                    className="mb-6 flex items-center justify-between"
                    data-tourid="policy-list-toolbar"
                >
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-semibold text-2xl">
                                ນະໂຍບາຍບໍລິສັດ
                            </h1>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="size-7 rounded-full"
                                onClick={tour.startTour}
                                title="ເລີ່ມທົດສອບການນຳທາງ"
                                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                            >
                                <HelpCircle className="size-5" />
                            </Button>
                        </div>
                        <p className="text-muted-foreground text-sm">
                            ຈັດການລະບຽບ ນະໂຍບາຍ ແລະ ຄູ່ມືການປະຕິບັດ
                        </p>
                    </div>
                    {canCreate && (
                        <Button
                            variant="default"
                            onClick={handleNewPolicy}
                            className="gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            ເພີ່ມນະໂຍບາຍ
                        </Button>
                    )}
                </div>

                <div
                    className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
                    data-tourid="policy-list-filter"
                >
                    <PolicyFilter
                        searchParams={{ ...search, limit: search.limit ?? 20 }}
                        fromRoute="/app/hr/company-policy"
                    />
                    <div data-tourid="policy-list-table">
                        <PolicyTable
                        data={data}
                        isLoading={listQuery.isLoading}
                        offset={offset}
                        limit={limit}
                        totalCount={totalCount}
                        onPaginationChange={handlePaginationChange}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSortingChange={handleSortingChange}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={(canDelete || canUpdate) ? handleDelete : undefined}
                        onArchive={canUpdate ? handleArchive : undefined}
                        onDuplicate={canCreate ? handleDuplicate : undefined}
                    />
                    </div>
                </div>
            </Main>
        </>
    );
}
